import {
	DocumentSigningRoleType,
	DocumentWorkflowInstanceStatus,
	ESignProvider,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import { isConcludedStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils';
import { formatUserFullName } from '@dealstack/shared/lib/format/user';
import { isOutstandingSignatureStatus } from '@dealstack/shared/lib/common/business/signatureStatus';
import type {
	CanRemoveSignerInput,
	DescribeRemovalInput,
	OutstandingSignatureGroup,
	RemovalDescription,
	RemovalSlot,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/RemoveSigner/removeSigner.utils.types';

// Mirrors the backend's CLOSED_STATUSES: the document will not progress, so it has no
// outstanding requirement to remove.
const CLOSED_STATUSES: ReadonlySet<DocumentWorkflowInstanceStatus> = new Set([
	DocumentWorkflowInstanceStatus.COMPLETED,
	DocumentWorkflowInstanceStatus.CANCELLED,
	DocumentWorkflowInstanceStatus.REJECTED,
	DocumentWorkflowInstanceStatus.EXPIRED,
	DocumentWorkflowInstanceStatus.ERROR,
]);

/**
 * Mirrors the backend's `hasOutstandingSignatures`: any signature anywhere on the document
 * still waiting on its signer.
 *
 * `status` is a signer's first signature only, where the backend scans all of them; there is one
 * per signer per instance in practice, and the nested `witnesses` cover the only real fan-out.
 */
export const hasOutstandingSignatures = (groups: readonly OutstandingSignatureGroup[]): boolean =>
	groups.some((group) =>
		group.signers.some(
			(signer) =>
				isOutstandingSignatureStatus(signer.status) ||
				(signer.witnesses ?? []).some((witness) =>
					isOutstandingSignatureStatus(witness.status)
				)
		)
	);

/**
 * Mirrors the backend's `checkRemoveSignerPreconditions`. Hiding the menu item is a courtesy — the
 * server refuses independently — so this only has to agree, not be trusted.
 */
export const canRemoveSigner = ({
	signerId,
	roleType,
	workflowStatus,
	signatureType,
	eSignProvider,
	cascadeStatuses,
	remainingSignerCount,
	remainingPartyCount,
	hasOutstandingSignatures: documentHasOutstandingSignatures,
}: CanRemoveSignerInput): boolean => {
	if (!signerId || CLOSED_STATUSES.has(workflowStatus)) {
		return false;
	}
	// Mirrors the backend: eID Easy is the only electronic provider a removal can be told about.
	if (signatureType === SignatureType.ELECTRONIC && eSignProvider !== ESignProvider.EID_EASY) {
		return false;
	}
	// Counterparties are unassigned, not removed.
	if (roleType === DocumentSigningRoleType.COUNTERPARTY) {
		return false;
	}
	if (!documentHasOutstandingSignatures) {
		return false;
	}
	if (cascadeStatuses.some(isConcludedStatus)) {
		return false;
	}
	if (roleType === DocumentSigningRoleType.PARTY && remainingPartyCount === 0) {
		return false;
	}
	return remainingSignerCount > 0;
};

/** A label only when there is something to show: a blank or whitespace-only value is no label. */
const toDisplayLabel = (value?: string | null): string[] => {
	const trimmed = value?.trim();
	return trimmed ? [trimmed] : [];
};

const toSlotLabel = (slot: {
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
}): string[] => {
	const fullName = toDisplayLabel(formatUserFullName(slot));
	return fullName.length ? fullName : toDisplayLabel(slot.email);
};

/** Every slot the removal would take, as its size and its display labels. */
export const describeRemoval = ({ signer, roleType }: DescribeRemovalInput): RemovalDescription => {
	// Only a party carries its investor's PIV-side slots out with it.
	const candidates: Array<RemovalSlot | undefined> = [
		signer,
		...(roleType === DocumentSigningRoleType.PARTY ? [signer.piv, signer.representative] : []),
	];
	const slots = candidates.filter((slot): slot is RemovalSlot => !!slot);
	const witnesses = slots.flatMap((slot) => slot.witnesses ?? []);

	return {
		slotCount: slots.length + witnesses.length,
		names: [
			...slots.flatMap(toSlotLabel),
			...witnesses.flatMap((witness) => toDisplayLabel(witness.email)),
		],
	};
};
